
import { EventManagementAPI } from "@/services/api/EventManagement.service";
import {
  defineComponent,
  onMounted,
  ref,
  toRef,
  computed,
  reactive,
  PropType,
  watch,
} from "vue";
import Editor from "@tinymce/tinymce-vue";
import { StepperComponent } from "@/assets/ts/components/_StepperComponent";
import { useForm } from "vee-validate";
import { Field, ErrorMessage, useIsFormValid } from "vee-validate";
import * as Yup from "yup";
import { hideModal } from "@/core/helpers/dom";
import { getIllustrationsPath } from "@/core/helpers/assets";
import { IEventManagement } from "@/interfaces/iEventManagement";
import { TranslationService } from "@/core/services/TranslationService";
import VZMultiSelect from "@/components/modals/forms/VZMultiSelect.vue";
import Multiselect from "@vueform/multiselect";
import VueMultiselect from "vue-multiselect";
import { useToast } from "vue-toastification";
import { useClipboard } from "@vueuse/core";
import { nextTick } from "vue";
import moment from "moment";
import { FormatMethods, getMaxLengthErrorMessage } from "@/helpers/data-format";
// import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
interface NewCCData {
  CC: Array<string>;
}
interface NewBCCData {
  BCC: Array<string>;
}
interface NewTOData {
  TO: Array<string>;
}
interface DataToStore {
  recipientType: string;
  recipient: string;
}
interface itemBody {
  subject: string;
  body: string;
  subjectLength: number;
  bodyLength: number;
}
interface Step1 {
  eventName: string;
}

interface Step2 {
  communicationMode: string;
}

interface Step3 {
  days_management: string;
}

// Define interfaces for type safety
interface ScheduleItem {
  value: string | number;
  text: string;
}

interface ProviderItem {
  value: string;
  text: string;
}

interface DynamicShortcutItem {
  text: string;
  value: string | number;
}

const communication_modes = ref([
  {
    code: "SMS",
    name: "SMS",
  },
  {
    code: "EMAIL",
    name: "Email",
  },
]);

interface KTCreateApp extends Step1, Step2, Step3 {}

interface TemplateItem {
  text: string;
  value: string | number;
}

export default defineComponent({
  name: "create-app-modal",
  components: {
    Field,
    ErrorMessage,
    editor: Editor,
    VZMultiSelect,
    Multiselect,
    VueMultiselect,
    // Datepicker,
  },
  emits: ["on-submit"],
  props: {
    singleEvent: {
      type: Object as PropType<IEventManagement>,
      default: null,
    },
    eventsList: {
      type: Array as PropType<IEventManagement[]>,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    let EventTypeName = "";
    const submitButton = ref<HTMLButtonElement | null>(null);
    const editEvent = ref<boolean>(false);
    const formRef = ref<null | any>(null);
    const copyButtonRef = ref<null | HTMLElement>(null);
    const editorRef = ref<null | HTMLElement>(null);
    const inputRef = ref<any>(null);
    const selectAll = ref(false);
    const singleEventToEdit = toRef(props, "singleEvent");
    const displayDateTime = computed(() => {
      return process.env.VUE_APP_DISPLAY_TIME || "MM-DD-YYYY HH:mm";
    });
    const startDate = ref("");
    const startTime = ref("");
    const endDate = ref("");
    const endTime = ref("");
    const repeat = ref(true);
    const minDate = computed(() => {
      return moment().format("YYYY-MM-DD");
    });
    const minEndDate = computed(() => {
      const today = moment().format("YYYY-MM-DD");
      const startD = moment(startDate.value).format("YYYY-MM-DD");
      if (today >= startD) {
        return today;
      } else {
        return startD;
      }
    });
    // Update ref declarations to avoid `any`

    const repeatEveryHourWeekly = ref<string | number | null>(null);
    const repeatEveryHourMonthly = ref<string | number | null>(null);
    const repeatEveryHourQuarterly = ref<string | number | null>(null);

    const data = reactive({
      mode: "EMAIL",
      state: "",
    });
    const mode = ref("EMAIL");
    const targetReceipentCC = ref<NewCCData>({
      CC: [],
    });
    const targetReceipentBCC = ref<NewBCCData>({
      BCC: [],
    });
    const targetReceipentTO = ref<NewTOData>({
      TO: [],
    });
    const loading = ref(false);
    const _stepperObj = ref<StepperComponent | null>(null);

    const items = ref<ScheduleItem[]>([]);
    const subTypeItems = ref<ScheduleItem[]>([]);
    const emailProviders = ref<ProviderItem[]>([]);
    const allDaysOfWeek = ref([
      "Sat",
      "Sun",
      "Mon",
      "Tue",
      "Wed",
      "Thu",
      "Fri",
    ]);
    const checkedDaysOfWeek = ref<Array<string>>([]);
    const checkedDayOfMonth = ref("");
    const checkedDayOfQuarter = ref("");
    const checkedDayOfYear = ref(false);
    const resultingCronExpression = ref("");
    const dayOfMonth = ref("");
    const dayOfQuarter = ref("");
    const toast = useToast();
    const eventAdded = t("pages.event.eventAdded");
    const eventUpdated = t("pages.event.eventUpdated");
    const clipboardCopyNotSupported = t(
      "pages.event.clipboardCopyNotSupported"
    );
    const cronYear = ref();
    const itemsTemplates = ref<TemplateItem[]>([]);
    const templateID = ref<string | number>("");

    const itemsReceipents = ref([]);
    const phoneItemsReceipents = ref([]);
    const typePlaceholder = ref("Select Type");
    const allReceipents = ref([
      {
        recipientType: "",
        recipient: "",
      },
    ]);
    const itemsBody = ref<itemBody>({
      subject: "",
      body: "",
      bodyLength: 0,
      subjectLength: 0,
    });
    const statusOptions = [
      {
        label: t("common.active"),
        value: "Active",
      },
      {
        label: t("common.inactive"),
        value: "Inactive",
      },
    ];
    const itemsDynamicShortcuts = ref<DynamicShortcutItem[]>([]);
    const createAppRef = ref<HTMLElement | null>(null);
    const myHtmlElem = ref<HTMLElement | any>(null);
    const createEventModalRef = ref<HTMLElement | null>(null);
    const currentStepIndex = ref(1);

    const formData = ref<KTCreateApp>({
      eventName: "",
      communicationMode: "EMAIL",
      days_management: "1",
    });
    const myWeekly = ref(true);
    const myMonthly = ref(true);
    const myQuarterly = ref(true);
    const myYearly = ref(true);
    const scheduleType = ref("null");
    const scheduleTypeValue = ref(null);
    const eventID = ref<number>();
    const receipentsToStore = ref<DataToStore>({
      recipientType: "",
      recipient: "",
    });
    const receipentsCCStore = ref<DataToStore>({
      recipientType: "",
      recipient: "",
    });
    const receipentsBCCStore = ref<DataToStore>({
      recipientType: "",
      recipient: "",
    });

    const format = computed(() => {
      return FormatMethods;
    });

    const data_dummy = ref<IEventManagement>({
      webId: 1,
      startDate: "",
      endDate: "",
      eventName: "",
      eventType: "",
      scheduleType: "null",
      eventSubType: "",
      templateName: "",
      eventTypeCodeText: "",
      eventGroup: "100",
      communicationType: "",
      schedule: "",
      subject: "",
      body: "",
      status: "Active",
      provider: "",
      bodyChange: false,
      eventRecipientModels: [
        {
          recipientType: "",
          recipient: "",
        },
      ],
    });
    const clipboardSource = computed(() => selectedShortcut.value?.value || "");
    const { copy, isSupported } = useClipboard({
      source: clipboardSource,
    });
    const events = toRef(props, "eventsList");
    const createAppSchema = [
      {},
      Yup.object({
        eventName: Yup.string()
          .max(30, getMaxLengthErrorMessage(t("common.name"), 30))
          .required("Name is required"),
        eventType: Yup.string().required("Event type is required"),
        eventSubType: Yup.string().required("Event sub type is required"),
        provider: Yup.string().required("Provider is required"),
        status: Yup.string().required("Status is required"),
      }),
      Yup.object({
        // communicationMode: Yup.string().required(
        //   "Communication mode is required"
        // ),
        // TO strings
        TO: Yup.array()
          .of(Yup.string())
          .label("TO")
          .when("communicationMode", {
            is: "EMAIL",
            then: Yup.array()
              .nullable()
              .of(Yup.string())
              .min(1, "To must have at least 1 item.")
              .nullable()
              .label("TO"),
          }),
        // TO: Yup.array()
        //   .nullable()
        //   .of(Yup.string().required())
        //   .min(1, "To must have at least 1 item.")
        //   .label("To"),
      }),
      Yup.object({
        days_management: Yup.string().required("Days management is required"),
        // startDate: Yup.string().required().label("Start Date"),
        // startTime: Yup.string().required().label("Start Time"),
        startDate: Yup.date()
          .transform((value, originalValue) =>
            originalValue === "" ? null : value
          )
          .nullable()
          .required(t("common.startDate") + " " + t("common.isRequired")),
        endDate: Yup.date()
          .transform((value, originalValue) =>
            originalValue === "" ? null : value
          )
          .nullable()
          .min(Yup.ref("startDate"), t("validation.greaterEndDate"))
          .required(t("common.endDate") + " " + t("common.isRequired")),
      }),
    ];
    const disablePreviousDates = (time) => {
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0); // Set time to midnight

      return time.getTime() < currentDate.getTime();
    };
    const currentSchema = computed(() => {
      return createAppSchema[currentStepIndex.value];
    });
    const recipientOptions = computed(() => {
      if (mode.value === "EMAIL") {
        return itemsReceipents.value;
      }
      return phoneItemsReceipents.value;
    });
    const totalSteps = computed(() => {
      if (!_stepperObj.value) {
        return;
      }

      return _stepperObj.value.totatStepsNumber;
    });
    // function keepPickerOpen(event) {
    //   // event.preventDefault();
    // }
    const { resetForm, handleSubmit, validate, errors } = useForm<
      Step1 | Step2 | Step3
    >({
      validationSchema: currentSchema,
    });

    const previousStep = () => {
      if (!_stepperObj.value) {
        return;
      }
      if (currentStepIndex.value == 4 && data_dummy.value.eventSubType != "9") {
        currentStepIndex.value = currentStepIndex.value - 2;
        _stepperObj.value.goto(2);
        return;
      }

      currentStepIndex.value--;

      _stepperObj.value.goPrev();
    };
    const isValid = useIsFormValid();
    const moveToSpecificStep = async (step: number) => {
      if (
        !_stepperObj.value ||
        !editEvent.value ||
        step === currentStepIndex.value
      ) {
        return;
      }
      // check if step and current step has invalid form in between
      if (step - currentStepIndex.value > 1) {
        for (let i = currentStepIndex.value + 1; i < step; i++) {
          const res = await validate(createAppSchema[i]);
          if (!res.valid) {
            return;
          }
        }
      } else {
        for (let i = step; i < currentStepIndex.value; i++) {
          const res = await validate(createAppSchema[i]);
          if (!res.valid) {
            return;
          }
        }
      }
      validate().then((res) => {
        if (!res.valid) {
          return;
        }
        if (!isValid.value) {
          return;
        }
        currentStepIndex.value = step;
        _stepperObj.value?.goto(step);
      });
    };

    const handleStep = handleSubmit(async (values) => {
      // Ensure required fields are present and check for event uniqueness
      if (currentStepIndex.value === 1) {
        const eventName = data_dummy.value.eventName?.trim() || "";
        const eventType = data_dummy.value.eventType?.trim() || "";
        const eventSubType = data_dummy.value.eventSubType?.trim() || "";

        // Check if required fields are filled
        if (!eventName || !eventType || !eventSubType) {
          toast.error("Please fill in all required fields");
          await validate();
          return;
        }

        // Normalize form's eventSubType using subTypeItems
        const selectedSubType = subTypeItems.value.find(
          (item) => item.value === eventSubType
        );
        const normalizedFormSubType = selectedSubType
          ? selectedSubType.text.toLowerCase().replace(/ /g, "_").toUpperCase()
          : eventSubType.toUpperCase();

        // Check for existing event
        const eventExists = events.value.some((event: IEventManagement) => {
          if (editEvent.value && event.webId === eventID.value) return false;

          const normalizedEventSubType = event.eventSubType
            ? event.eventSubType.toUpperCase()
            : "";

          if (eventType === "2") {
            return (
              event.eventName?.trim() === eventName &&
              event.eventType === eventType &&
              normalizedEventSubType === normalizedFormSubType
            );
          }
          return (
            event.eventType === eventType &&
            normalizedEventSubType === normalizedFormSubType
          );
        });

        if (eventExists) {
          if (eventType === "2") {
            toast.error(
              "An event with this name, type, and subtype already exists"
            );
          } else {
            toast.error("An event with this type and subtype already exists");
          }
          await validate();
          return;
        }
      }

      // Validate date for Step 3
      if (currentStepIndex.value === 3) {
        if (!startDate.value && data_dummy.value.eventType !== "0") {
          toast.error("Please select start date & time to continue");
          await validate();
          return;
        } else if (!repeat.value) {
          resultingCronExpression.value = "0 0 0 ? * *";
          formatFinalExpressions();
        }
      }

      // Update form data
      formData.value = {
        ...formData.value,
        ...values,
      };

      // Skip Step 3 if eventSubType is not "9"
      if (
        currentStepIndex.value === 2 &&
        data_dummy.value.eventSubType !== "9"
      ) {
        currentStepIndex.value += 2;
        if (_stepperObj.value) {
          _stepperObj.value.goto(4);
        }
        return;
      }

      // Proceed to next step
      currentStepIndex.value++;
      if (_stepperObj.value) {
        _stepperObj.value.goNext();
      }
    });
    // Update getTemplateID to use the event parameter or remove it if unused
    const getTemplateID = async (event: Event) => {
      templateID.value = (event.target as HTMLSelectElement).value;
    };

    const getScheduleTypeData = async () => {
      items.value = [];
      const data =
        await EventManagementAPI.getInstance().getAllScheduleEventTypes();
      const formattedData = data.map((item) => ({
        value: item.value,
        text: item.text
          .toLowerCase()
          .replace(/_/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase()),
      }));
      items.value = formattedData;
    };
    const getScheduleEventSubTypeData = async () => {
      if (data_dummy.value.eventType.length > 0) {
        subTypeItems.value = [];

        const data =
          await EventManagementAPI.getInstance().getAllScheduleEventSubTypes(
            data_dummy.value.eventType
          );
        const formattedData = data.map((item) => ({
          value: item.value,
          text: item.text
            .toLowerCase()
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase()),
        }));
        subTypeItems.value = formattedData;
      }
    };
    const getProviders = async () => {
      emailProviders.value = [];
      const data =
        await EventManagementAPI.getInstance().getAllScheduleProviders();
      emailProviders.value = data;
    };
    const getScheduleTemplateData = async () => {
      itemsTemplates.value = [];
      const matchingSubTypeItemText = subTypeItems.value.find(
        (item) => item.value === data_dummy.value.eventSubType
      );
      if (matchingSubTypeItemText) {
        const data =
          await EventManagementAPI.getInstance().getAllScheduleTemplates(
            matchingSubTypeItemText.text
          );
        itemsTemplates.value = data;
      }
    };
    const getBody = async () => {
      const data = await EventManagementAPI.getInstance().getAllScheduleBody(
        templateID.value
      );
      itemsBody.value.subject = data.subject;
      itemsBody.value.subjectLength = data.subject.length;
      itemsBody.value.body = data.body;
      itemsBody.value.bodyLength = data.body.length;
    };
    const getScheduleDynamicShortcutsData = async () => {
      const data =
        await EventManagementAPI.getInstance().getAllScheduleDynamicShortcuts();
      itemsDynamicShortcuts.value = data;
    };
    const changeMonthly = async () => {
      resultingCronExpression.value = "0 0 0 " + dayOfMonth.value + " * ?";
      formatFinalExpressions();
    };
    const changeQuarterly = async () => {
      resultingCronExpression.value = "0 0 0 " + dayOfQuarter.value + " */3 ?";
      formatFinalExpressions();
    };
    const changeYearly = async (event) => {
      scheduleType.value = "Yearly";
      if (event && event.target.checked) {
        formatFinalExpressions();
      } else {
        resultingCronExpression.value = "0 0 0 ? * *";
        formatFinalExpressions();
      }
    };
    const changeWeekly = () => {
      scheduleType.value = "Weekly";
      let days = "";
      checkedDaysOfWeek.value.forEach((value, index) => {
        if (index == 0) {
          days = value;
        } else if (index > 0) {
          days = days + "," + value;
        }
      });
      resultingCronExpression.value = "0 0 0 ? * " + days;
      formatFinalExpressions();
    };
    const toggleSelectAll = () => {
      if (selectAll.value) {
        checkedDaysOfWeek.value = [...allDaysOfWeek.value];
      } else {
        checkedDaysOfWeek.value = []; // Deselect all days
      }
      changeWeeklyAll();
    };

    const changeWeeklyAll = () => {
      const days = checkedDaysOfWeek.value.join(",");
      resultingCronExpression.value = `0 0 0 ? * ${days}`;
      formatFinalExpressions();
    };
    // Update onTypeChange to avoid `any`
    const onTypeChange = async (value: string | number) => {
      const selected = items.value.find((type) => type.value == value);
      data_dummy.value.eventTypeCodeText = selected?.text ?? "";
      data_dummy.value.eventSubType = "";
      templateID.value = "";
      itemsBody.value.subject = "";
      itemsBody.value.body = "";
      await getScheduleEventSubTypeData();
      formatFinalExpressions();
    };

    const onSubTypeChange = async (value: string | number) => {
      await getScheduleTemplateData(); // Always get latest templates (safe)

      if (data_dummy.value.eventType && value) {
        const eventFormTemplate = itemsTemplates.value.find((template) =>
          template.text?.toLowerCase()
        );

        if (eventFormTemplate) {
          templateID.value = eventFormTemplate.value;
          await getBody();
        } else {
          templateID.value = "";
          itemsBody.value.subject = "";
          itemsBody.value.body = "";
        }
      } else {
        templateID.value = "";
        itemsBody.value.subject = "";
        itemsBody.value.body = "";
      }

      formatFinalExpressions();
    };

    const onChange = async () => {
      const type = scheduleType.value;

      // Reset all flags first
      myWeekly.value = false;
      myMonthly.value = false;
      myQuarterly.value = false;
      myYearly.value = false;

      // Set only the active flag
      if (type === "Weekly") myWeekly.value = true;
      else if (type === "Monthly") myMonthly.value = true;
      else if (type === "Quarterly") myQuarterly.value = true;
      else if (type === "Yearly") myYearly.value = true;
      if (scheduleType.value == "Weekly") {
        checkedDayOfMonth.value = "";
        checkedDayOfQuarter.value = "";
        myWeekly.value = true;
        myMonthly.value = false;
        myQuarterly.value = false;
        myYearly.value = false;
      } else if (scheduleType.value == "Monthly") {
        checkedDaysOfWeek.value = [];
        checkedDayOfQuarter.value = "";
        myWeekly.value = false;
        myMonthly.value = true;
        myQuarterly.value = false;
        myYearly.value = false;
      } else if (scheduleType.value == "Quarterly") {
        checkedDaysOfWeek.value = [];
        checkedDayOfMonth.value = "";
        myWeekly.value = false;
        myMonthly.value = false;
        myQuarterly.value = true;
        myYearly.value = false;
      } else if (scheduleType.value == "Yearly") {
        checkedDaysOfWeek.value = [];
        checkedDayOfMonth.value = "";
        checkedDayOfQuarter.value = "";
        myWeekly.value = false;
        myMonthly.value = false;
        myQuarterly.value = false;
        myYearly.value = true;
      }
      formatFinalExpressions();
    };
    const formSubmit = async () => {
      // Handle eventSubType safely
      const selectedSubType = subTypeItems.value.find(
        (item) => item.value == data_dummy.value.eventSubType
      );
      data_dummy.value.eventSubType = selectedSubType?.text
        ? selectedSubType.text.toLowerCase().replace(/ /g, "_").toUpperCase()
        : data_dummy.value.eventSubType || "";

      if (
        itemsBody.value.body.length > 1 ||
        itemsBody.value.subject.length > 1
      ) {
        if (
          itemsBody.value.body.length != itemsBody.value.bodyLength ||
          itemsBody.value.subject.length != itemsBody.value.subjectLength
        ) {
          data_dummy.value.bodyChange = true;
        }
      }
      data_dummy.value.templateName = data_dummy.value.eventSubType;
      if (submitButton.value) {
        submitButton.value.disabled = true;
        submitButton.value.setAttribute("data-kt-indicator", "on");
      }
      const isValid = useIsFormValid();
      if (!isValid) {
        console.log("Form has errors.");
        return;
      }

      if (mode.value === "EMAIL") {
        data_dummy.value.communicationType = "Email";
        targetReceipentTO.value.TO.forEach((data) => {
          receipentsToStore.value.recipientType = "TO";
          receipentsToStore.value.recipient = data.toString();
          allReceipents.value.push({ ...receipentsToStore.value });
        });
      } else {
        data_dummy.value.communicationType = "SMS";
        targetReceipentTO.value.TO.forEach((value) => {
          receipentsToStore.value.recipientType = "TO";
          receipentsToStore.value.recipient = value.toString();
          allReceipents.value.push({ ...receipentsToStore.value });
        });
      }
      targetReceipentCC.value.CC.forEach((value) => {
        receipentsCCStore.value.recipientType = "CC";
        receipentsCCStore.value.recipient = value.toString();
        allReceipents.value.push({ ...receipentsCCStore.value });
      });
      targetReceipentBCC.value.BCC.forEach((value) => {
        receipentsBCCStore.value.recipientType = "BCC";
        receipentsBCCStore.value.recipient = value.toString();
        allReceipents.value.push({ ...receipentsBCCStore.value });
      });
      allReceipents.value.shift();

      data_dummy.value.subject = itemsBody.value.subject;
      data_dummy.value.body = itemsBody.value.body;

      const dateFormat = "YYYY-MM-DDTHH:mm:ss";
      if (startDate.value) {
        const startTimeX = moment(startDate.value).format(dateFormat);
        data_dummy.value.startDate = `${startTimeX}+00:00`;
      }
      if (endDate.value) {
        const endTimeX = moment(endDate.value).format(dateFormat);
        data_dummy.value.endDate = `${endTimeX}+00:00`;
      }

      // Only update schedule if valid data exists for the selected schedule type
      // Always send the type with selected values, not just the last opened tab
      if (repeat.value) {
        // Pure cron generators (do not mutate state)
        const getWeeklyCron = () => {
          let days = checkedDaysOfWeek.value.join(",");
          let hour = repeatEveryHourWeekly.value
            ? `*/${repeatEveryHourWeekly.value}`
            : "0";
          return `0 0 ${hour} ? * ${days}`;
        };
        const getMonthlyCron = () => {
          let hour = repeatEveryHourMonthly.value
            ? `*/${repeatEveryHourMonthly.value}`
            : "0";
          return `0 0 ${hour} ${dayOfMonth.value} * ?`;
        };
        const getQuarterlyCron = () => {
          let hour = repeatEveryHourQuarterly.value
            ? `*/${repeatEveryHourQuarterly.value}`
            : "0";
          return `0 0 ${hour} ${dayOfQuarter.value} */3 ?`;
        };
        const getYearlyCron = () => {
          // Add your yearly cron logic here if needed
          return `0 0 0 ? * *`;
        };
        // Helper to get the first valid schedule type and value (priority: Weekly > Monthly > Quarterly > Yearly)
        let validSchedule: { type: string; cron: string } | null = null;
        if (checkedDaysOfWeek.value && checkedDaysOfWeek.value.length > 0) {
          validSchedule = { type: "Weekly", cron: getWeeklyCron() };
        } else if (dayOfMonth.value) {
          validSchedule = { type: "Monthly", cron: getMonthlyCron() };
        } else if (dayOfQuarter.value) {
          validSchedule = { type: "Quarterly", cron: getQuarterlyCron() };
        } else if (checkedDayOfYear.value) {
          validSchedule = { type: "Yearly", cron: getYearlyCron() };
        }

        if (validSchedule) {
          data_dummy.value.schedule = validSchedule.cron;
          data_dummy.value.scheduleType = validSchedule.type;
        } else {
          data_dummy.value.schedule = "";
          data_dummy.value.scheduleType = "null";
        }
      } else {
        data_dummy.value.schedule = "";
        data_dummy.value.scheduleType = "null";
      }
      data_dummy.value.eventRecipientModels = allReceipents.value;
      delete (data_dummy.value as { status?: string }).status;
      // if (data_dummy.value.startDate) {
      //   let localDateTime = new Date(data_dummy.value.startDate);
      //   let localOffset = localDateTime.getTimezoneOffset();
      //   let utcDateTime = new Date(
      //     localDateTime.getTime() + localOffset * 60 * 1000
      //   );
      //   let year = utcDateTime.getUTCFullYear();
      //   let month = String(utcDateTime.getUTCMonth() + 1).padStart(2, "0");
      //   let day = String(utcDateTime.getUTCDate()).padStart(2, "0");
      //   let hours = String(utcDateTime.getUTCHours()).padStart(2, "0");
      //   let minutes = String(utcDateTime.getUTCMinutes()).padStart(2, "0");
      //   let seconds = String(utcDateTime.getUTCSeconds()).padStart(2, "0");
      //   let formattedUTC = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
      //   data_dummy.value.startDate = formattedUTC;
      // }
      // if (data_dummy.value.endDate) {
      //   let localDateTime = new Date(data_dummy.value.endDate);
      //   let localOffset = localDateTime.getTimezoneOffset();
      //   let utcDateTime = new Date(
      //     localDateTime.getTime() + localOffset * 60 * 1000
      //   );
      //   let year = utcDateTime.getUTCFullYear();
      //   let month = String(utcDateTime.getUTCMonth() + 1).padStart(2, "0");
      //   let day = String(utcDateTime.getUTCDate()).padStart(2, "0");
      //   let hours = String(utcDateTime.getUTCHours()).padStart(2, "0");
      //   let minutes = String(utcDateTime.getUTCMinutes()).padStart(2, "0");
      //   let seconds = String(utcDateTime.getUTCSeconds()).padStart(2, "0");
      //   let formattedUTC = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
      //   data_dummy.value.endDate = formattedUTC;
      // }
      if (singleEventToEdit.value.eventName) {
        data_dummy.value.webId = eventID.value;
        await EventManagementAPI.getInstance()
          .addEvent(data_dummy.value, editEvent.value)
          .then(() => {
            if (editEvent.value) {
              toast.success(eventUpdated);
            } else {
              toast.success(eventAdded);
            }
            emit("on-submit");
            const deleteModal = document.querySelector(
              `#kt_modal_create_event`
            ) as HTMLElement;
            hideModal(deleteModal);
            editEvent.value = false;
            resetAllFormValues();
          })
          .catch(() => {
            console.error("Error saving event");
          })
          .finally(() => {
            loading.value = false;
            if (submitButton.value) {
              submitButton.value.disabled = false;
              submitButton.value.setAttribute("data-kt-indicator", "off");
            }
          });
      } else {
        delete data_dummy.value.webId;
        await EventManagementAPI.getInstance()
          .addEvent(data_dummy.value, editEvent.value)
          .then(() => {
            if (editEvent.value) {
              toast.success(eventUpdated);
            } else {
              toast.success(eventAdded);
            }
            emit("on-submit");
            const deleteModal = document.querySelector(
              `#kt_modal_create_event`
            ) as HTMLElement;
            hideModal(deleteModal);
            editEvent.value = false;
            resetAllFormValues();
          })
          .catch(() => {
            console.error("Error saving event");
          })
          .finally(() => {
            loading.value = false;
            if (submitButton.value) {
              submitButton.value.disabled = false;
              submitButton.value.setAttribute("data-kt-indicator", "off");
            }
          });
      }
    };

    const formatFinalExpressions = () => {
      const hours = moment(startDate.value).format("H");
      let cronExpression = resultingCronExpression.value || "0 0 0 ? * *";
      let cronExpressionArray = cronExpression.split(" ");
      cronExpressionArray[0] = "0"; // Seconds
      cronExpressionArray[1] = "0"; // Minutes
      cronExpressionArray[2] = hours; // Use hours from startDate
      resultingCronExpression.value = cronExpressionArray.join(" ");
    };

    const addEveryHour = () => {
      formatFinalExpressions();
    };

    const resetAllFormValues = async () => {
      return new Promise((resolve) => {
        // clear data_dummy
        data_dummy.value = {
          webId: 1,
          startDate: "",
          endDate: "",
          eventName: "",
          eventType: "",
          scheduleType: "null",
          eventSubType: "",
          templateName: "",
          eventTypeCodeText: "",
          eventGroup: "",
          communicationType: "",
          schedule: "",
          subject: "",
          body: "",
          status: "Active",
          provider: "",
          eventRecipientModels: [],
          bodyChange: false,
        };
        targetReceipentTO.value.TO = [];
        targetReceipentCC.value.CC = [];
        targetReceipentBCC.value.BCC = [];
        itemsBody.value.subject = "";
        itemsBody.value.body = "";
        selectedShortcut.value = null;
        scheduleType.value = "null";
        checkedDaysOfWeek.value = [];
        checkedDayOfMonth.value = "";
        checkedDayOfQuarter.value = "";
        resultingCronExpression.value = "";
        _stepperObj.value?.goto(1);
        nextTick(async () => {
          await formRef.value?.reset();
          resetForm();
          resolve({});
        });
      });
    };

    const checkWeeklyCronExpression = async () => {
      myHtmlElem.value = document.querySelector("#weekly_management");
      checkedDaysOfWeek.value = [];
      const cronParts = resultingCronExpression.value.split(" ");
      if (cronParts[5] && cronParts[5] !== "*" && cronParts[5] !== "?") {
        const days = cronParts[5]
          .split(",")
          .filter((day) => allDaysOfWeek.value.includes(day));
        checkedDaysOfWeek.value = days;
        if (days.length > 0) {
          myHtmlElem.value.className += " show";
          scheduleType.value = "Weekly";
        }
        if (days.length === allDaysOfWeek.value.length) {
          selectAll.value = true;
        }
      }
      // Extract repeat hours
      if (cronParts[2] && cronParts[2].includes("/")) {
        repeatEveryHourWeekly.value = cronParts[2].split("/")[1];
      }
    };

    const checkMonthlyCronExpression = async () => {
      myHtmlElem.value = document.querySelector("#monthly_management");
      const cronExpression = resultingCronExpression.value;
      const cronParts = cronExpression.split(" ");
      if (cronExpression.includes(" 1 ") || cronExpression.includes(" 1,")) {
        myHtmlElem.value.className += " show";
        scheduleType.value = "Monthly";
        dayOfMonth.value = "1";
      } else if (
        cronExpression.includes(" 15 ") ||
        cronExpression.includes(" 15,")
      ) {
        myHtmlElem.value.className += " show";
        scheduleType.value = "Monthly";
        dayOfMonth.value = "15";
      } else if (cronExpression.includes(" L ")) {
        myHtmlElem.value.className += " show";
        scheduleType.value = "Monthly";
        dayOfMonth.value = "L";
      }
      // Extract repeat hours
      if (cronParts[2] && cronParts[2].includes("/")) {
        repeatEveryHourMonthly.value = cronParts[2].split("/")[1];
      }
    };

    const checkQuarterlyCronExpression = async () => {
      myHtmlElem.value = document.querySelector("#quarterly_management");
      const cronExpression = resultingCronExpression.value;
      const cronParts = cronExpression.split(" ");
      if (cronExpression.includes(" 1 ") || cronExpression.includes(" 1,")) {
        myHtmlElem.value.className += " show";
        scheduleType.value = "Quarterly";
        dayOfQuarter.value = "1";
      } else if (
        cronExpression.includes(" 15 ") ||
        cronExpression.includes(" 15,")
      ) {
        myHtmlElem.value.className += " show";
        scheduleType.value = "Quarterly";
        dayOfQuarter.value = "15";
      } else if (cronExpression.includes(" L ")) {
        myHtmlElem.value.className += " show";
        scheduleType.value = "Quarterly";
        dayOfQuarter.value = "L";
      }
      // Extract repeat hours
      if (cronParts[2] && cronParts[2].includes("/")) {
        repeatEveryHourQuarterly.value = cronParts[2].split("/")[1];
      }
    };

    const checkYearlyCronExpression = async () => {
      myHtmlElem.value = document.querySelector("#yearly_management");
      if (resultingCronExpression.value.indexOf("20") >= 0) {
        myHtmlElem.value.className += " show";
        scheduleType.value = "Yearly";
        checkedDayOfYear.value = true;
      }
    };

    resetForm({
      values: {
        ...formData.value,
      },
    });

    onMounted(async () => {
      formRef.value?.reset();
      resetForm();
      _stepperObj.value = StepperComponent.createInsance(
        createAppRef.value as HTMLElement
      );
      getScheduleTypeData();
      getProviders();
      // getScheduleReceipentsData();
      getScheduleDynamicShortcutsData();
    });
    const selectAShortcut = t("common.selectAShortcut");
    const copyText = () => {
      if (!clipboardSource.value) {
        toast.error(selectAShortcut);
        return;
      }

      if (isSupported) {
        copy();
        const buttonCaption = copyButtonRef.value?.innerHTML;
        const multiselectEl = inputRef.value?.$el;
        if (multiselectEl) {
          multiselectEl.classList.add("text-inverse-success");
        }

        if (copyButtonRef.value) {
          copyButtonRef.value.innerHTML = "Copied!";
        }

        setTimeout(() => {
          if (copyButtonRef.value && buttonCaption) {
            copyButtonRef.value.innerHTML = buttonCaption;
          }
          if (multiselectEl) {
            multiselectEl.classList.remove("text-inverse-success");
          }
        }, 3000);
      } else {
        toast.error(clipboardCopyNotSupported);
      }
    };
    watch(
      () => mode,
      () => (data.state = "")
    );
    watch(
      singleEventToEdit,
      async (eventObj: IEventManagement) => {
        try {
          editEvent.value = eventObj.webId !== 1;
          const [data, phone] = await Promise.all([
            EventManagementAPI.getInstance().getAllScheduleReceipents(),
            EventManagementAPI.getInstance().getPhoneScheduleReceipents(),
          ]);
          itemsReceipents.value = data;
          phoneItemsReceipents.value = phone;

          await resetAllFormValues();
          mode.value = eventObj.communicationType === "SMS" ? "SMS" : "EMAIL";
          await nextTick();

          if (eventObj.eventRecipients) {
            eventObj.eventRecipients.forEach((value) => {
              if (value.recipient_type === "TO") {
                targetReceipentTO.value.TO.push(value.recipient);
              } else if (
                value.recipient_type === "CC" &&
                mode.value === "EMAIL"
              ) {
                targetReceipentCC.value.CC.push(value.recipient);
              } else if (
                value.recipient_type === "BCC" &&
                mode.value === "EMAIL"
              ) {
                targetReceipentBCC.value.BCC.push(value.recipient);
              }
            });
          }

          data_dummy.value.eventName = eventObj.eventName;
          data_dummy.value.eventType = eventObj.eventType;
          await getScheduleEventSubTypeData();

          if (editEvent.value) {
            const formattedSubType = eventObj.eventSubType
              .toLowerCase()
              .replace(/_/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase());
            data_dummy.value.eventSubType = String(
              subTypeItems.value.find((item) => item.text === formattedSubType)
                ?.value || eventObj.eventSubType
            );
          }

          await getScheduleTemplateData();

          if (data_dummy.value.eventType && data_dummy.value.eventSubType) {
            const eventFormTemplate = itemsTemplates.value.find((template) => {
              const text = template.text?.toLowerCase() || "";
              return text;
            });
            if (eventFormTemplate) {
              templateID.value = String(eventFormTemplate.value);
              if (eventObj.subject && eventObj.body) {
                itemsBody.value.subject = eventObj.subject;
                itemsBody.value.body = eventObj.body;
              } else {
                await getBody();
              }
            }
          } else {
            templateID.value = "";
            itemsBody.value.subject = eventObj.subject || "";
            itemsBody.value.body = eventObj.body || "";
          }

          data_dummy.value.status =
            eventObj.status.charAt(0).toUpperCase() +
            eventObj.status.slice(1).toLowerCase();
          data_dummy.value.provider = eventObj.provider;
          data_dummy.value.eventTypeCodeText = eventObj.eventTypeCodeText;
          eventID.value = eventObj.webId;
          data_dummy.value.schedule = eventObj.schedule || "";
          resultingCronExpression.value = eventObj.schedule || "";
          data_dummy.value.scheduleType = eventObj.scheduleType || "Weekly";
          dayOfMonth.value = "";
          dayOfQuarter.value = "";
          checkedDayOfYear.value = false;
          currentStepIndex.value = 1;

          if (eventObj.scheduleType && eventObj.schedule) {
            scheduleType.value = eventObj.scheduleType;
            repeat.value = true;
            myWeekly.value = scheduleType.value === "Weekly";
            myMonthly.value = scheduleType.value === "Monthly";
            myQuarterly.value = scheduleType.value === "Quarterly";
            myYearly.value = scheduleType.value === "Yearly";
            const cronParts = eventObj.schedule.split(" ");
            // Clear all values first
            checkedDaysOfWeek.value = [];
            dayOfMonth.value = "";
            dayOfQuarter.value = "";
            repeatEveryHourWeekly.value = null;
            repeatEveryHourMonthly.value = null;
            repeatEveryHourQuarterly.value = null;
            selectAll.value = false;
            checkedDayOfYear.value = false;
            // Only restore the saved type and its values
            if (eventObj.scheduleType === "Weekly") {
              checkedDaysOfWeek.value =
                cronParts[5] && cronParts[5] !== "*" && cronParts[5] !== "?"
                  ? cronParts[5].split(",")
                  : [];
              if (
                checkedDaysOfWeek.value.length === allDaysOfWeek.value.length
              ) {
                selectAll.value = true;
              }
              if (cronParts[2] && cronParts[2].includes("/")) {
                repeatEveryHourWeekly.value = cronParts[2].split("/")[1];
              } else {
                repeatEveryHourWeekly.value = null;
              }
            } else if (eventObj.scheduleType === "Monthly") {
              dayOfMonth.value = cronParts[3] || "";
              if (cronParts[2] && cronParts[2].includes("/")) {
                repeatEveryHourMonthly.value = cronParts[2].split("/")[1];
              } else {
                repeatEveryHourMonthly.value = null;
              }
            } else if (eventObj.scheduleType === "Quarterly") {
              dayOfQuarter.value = cronParts[3] || "";
              if (cronParts[2] && cronParts[2].includes("/")) {
                repeatEveryHourQuarterly.value = cronParts[2].split("/")[1];
              } else {
                repeatEveryHourQuarterly.value = null;
              }
            } else if (eventObj.scheduleType === "Yearly") {
              checkedDayOfYear.value = true;
            }
          } else {
            scheduleType.value = "Weekly";
            repeat.value = true;
            checkedDaysOfWeek.value = [];
            selectAll.value = false;
            dayOfMonth.value = "";
            dayOfQuarter.value = "";
            repeatEveryHourWeekly.value = null;
            repeatEveryHourMonthly.value = null;
            repeatEveryHourQuarterly.value = null;
            checkedDayOfYear.value = false;
            myWeekly.value = true;
            myMonthly.value = false;
            myQuarterly.value = false;
            myYearly.value = false;
          }

          if (eventObj.startDate) {
            const formattedStartDate = format.value.Date(
              eventObj.startDate,
              "YYYY-MM-DD HH:mm"
            );
            startDate.value = formattedStartDate;
          }
          if (eventObj.endDate) {
            const formattedEndDate = format.value.Date(
              eventObj.endDate,
              "YYYY-MM-DD HH:mm"
            );
            endDate.value = formattedEndDate;
          }
        } catch (error) {
          console.error("Error in singleEventToEdit watch:", error);
          toast.error("Failed to load event data. Please try again.");
        }
      },
      { deep: true, immediate: true }
    );

    // watch(
    //   singleEventToEdit,
    //   (newValue) => {
    //     if (newValue.webId == 1) {
    //       resetForm();
    //     }
    //   },
    //   { deep: true }
    // );
    watch(
      checkedDaysOfWeek,
      (newValue) => {
        if (checkedDaysOfWeek.value.length == 7) {
          selectAll.value = true;
        } else {
          selectAll.value = false;
        }
      },
      { deep: true, immediate: true }
    );

    const weeklyStore = reactive<{
      checkedDays: string[];
      repeatEveryHour: string | number | null;
      selectAll: boolean;
    }>({
      checkedDays: [],
      repeatEveryHour: null,
      selectAll: false,
    });
    const monthlyStore = reactive<{
      dayOfMonth: string;
      repeatEveryHour: string | number | null;
    }>({
      dayOfMonth: "",
      repeatEveryHour: null,
    });
    const quarterlyStore = reactive<{
      dayOfQuarter: string;
      repeatEveryHour: string | number | null;
    }>({
      dayOfQuarter: "",
      repeatEveryHour: null,
    });
    const yearlyStore = reactive({
      checked: false,
    });

    const handleScheduleTypeChange = (type: string) => {
      if (scheduleType.value === "Weekly") {
        weeklyStore.checkedDays = [...checkedDaysOfWeek.value];
        weeklyStore.repeatEveryHour = repeatEveryHourWeekly.value;
        weeklyStore.selectAll = selectAll.value;
      } else if (scheduleType.value === "Monthly") {
        monthlyStore.dayOfMonth = dayOfMonth.value;
        monthlyStore.repeatEveryHour = repeatEveryHourMonthly.value;
      } else if (scheduleType.value === "Quarterly") {
        quarterlyStore.dayOfQuarter = dayOfQuarter.value;
        quarterlyStore.repeatEveryHour = repeatEveryHourQuarterly.value;
      } else if (scheduleType.value === "Yearly") {
        yearlyStore.checked = checkedDayOfYear.value;
      }

      scheduleType.value = type;
      myWeekly.value = type === "Weekly";
      myMonthly.value = type === "Monthly";
      myQuarterly.value = type === "Quarterly";
      myYearly.value = type === "Yearly";

      if (type === "Weekly") {
        checkedDaysOfWeek.value = [...weeklyStore.checkedDays];
        repeatEveryHourWeekly.value = weeklyStore.repeatEveryHour;
        selectAll.value = weeklyStore.selectAll;
        if (checkedDaysOfWeek.value.length > 0) changeWeekly();
      } else if (type === "Monthly") {
        dayOfMonth.value = monthlyStore.dayOfMonth;
        repeatEveryHourMonthly.value = monthlyStore.repeatEveryHour;
        if (dayOfMonth.value) changeMonthly();
      } else if (type === "Quarterly") {
        dayOfQuarter.value = quarterlyStore.dayOfQuarter;
        repeatEveryHourQuarterly.value = quarterlyStore.repeatEveryHour;
        if (dayOfQuarter.value) changeQuarterly();
      } else if (type === "Yearly") {
        checkedDayOfYear.value = yearlyStore.checked;
        if (checkedDayOfYear.value)
          changeYearly({ target: { checked: checkedDayOfYear.value } });
      }
    };
    const beforeEnter = (el) => {
      el.style.height = "0";
      el.style.opacity = "0";
    };
    const enter = (el) => {
      el.style.transition = "height 0.35s ease, opacity 0.35s ease";
      el.style.height = el.scrollHeight + "px";
      el.style.opacity = "1";
    };
    const afterEnter = (el) => {
      el.style.height = "auto";
      el.style.transition = "";
    };
    const beforeLeave = (el) => {
      el.style.height = el.scrollHeight + "px";
      el.style.opacity = "1";
    };
    const leave = (el) => {
      el.style.transition = "height 0.35s ease, opacity 0.35s ease";
      // Force reflow
      void el.offsetHeight;
      el.style.height = "0";
      el.style.opacity = "0";
    };
    const afterLeave = (el) => {
      el.style.transition = "";
    };
    const selectedShortcut = ref<{ label: string; value: string } | null>(null);

    watch(
      templateID,
      (newValue) => {
        if (!newValue) {
          itemsBody.value.subject = "";
          itemsBody.value.body = "";
          itemsBody.value.subjectLength = 0;
          itemsBody.value.bodyLength = 0;
          data_dummy.value.bodyChange = false;
        }
      },
      { immediate: false }
    );
    return {
      eventID,
      myHtmlElem,
      targetReceipentTO,
      targetReceipentBCC,
      targetReceipentCC,
      data_dummy,
      handleStep,
      formSubmit,
      previousStep,
      createAppRef,
      currentStepIndex,
      totalSteps,
      createEventModalRef,
      getIllustrationsPath,
      data,
      communication_modes,
      loading,
      items,
      itemsTemplates,
      itemsReceipents,
      itemsBody,
      itemsDynamicShortcuts,
      templateID,
      getBody,
      scheduleType,
      scheduleTypeValue,
      onChange,
      myWeekly,
      myMonthly,
      myQuarterly,
      myYearly,
      cronYear,
      checkedDaysOfWeek,
      checkedDayOfMonth,
      checkedDayOfQuarter,
      resultingCronExpression,
      changeWeekly,
      changeMonthly,
      changeQuarterly,
      changeYearly,
      dayOfMonth,
      dayOfQuarter,
      receipentsToStore,
      receipentsCCStore,
      receipentsBCCStore,
      allReceipents,
      resetAllFormValues,
      singleEventToEdit,
      getTemplateID,
      copyButtonRef,
      inputRef,
      checkWeeklyCronExpression,
      checkMonthlyCronExpression,
      checkQuarterlyCronExpression,
      checkYearlyCronExpression,
      checkedDayOfYear,
      typePlaceholder,
      t,
      editEvent,
      onTypeChange,
      onSubTypeChange,
      EventTypeName,
      copy,
      clipboardSource,
      copyText,
      currentSchema,
      mode,
      moveToSpecificStep,
      errors,
      editorRef,
      startDate,
      endDate,
      startTime,
      endTime,
      repeat,
      submitButton,
      phoneItemsReceipents,
      recipientOptions,
      statusOptions,
      repeatEveryHourWeekly,
      repeatEveryHourMonthly,
      repeatEveryHourQuarterly,
      formatFinalExpressions,
      addEveryHour,
      subTypeItems,
      getScheduleEventSubTypeData,
      emailProviders,
      getProviders,
      getScheduleTemplateData,
      minDate,
      minEndDate,
      // keepPickerOpen,
      displayDateTime,
      disablePreviousDates,
      allDaysOfWeek,
      toggleSelectAll,
      selectAll,
      weeklyStore,
      monthlyStore,
      quarterlyStore,
      yearlyStore,
      handleScheduleTypeChange,
      beforeEnter,
      enter,
      afterEnter,
      beforeLeave,
      leave,
      afterLeave,
      events,
      format,
      selectedShortcut,
    };
  },
});
